import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, ButtonRow, PrimaryButton } from "../../components/button";
import { Card, CardNavigation } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { DataPickerControl } from "../../components/data-picker";
import { Dashboard } from "../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { NumberInput, Select, TextArea } from "../../components/form";
import { Icon } from "../../components/icon";
import { Padding } from "../../components/layout";
import { ListPickerControl } from "../../components/list-picker";
import { Expander } from "../../components/expander";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent, PlaygroundDefault } from "../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../components/sizable-container";
import { TabControl } from "../../components/tabcontrol";
import { BlockCode, BlockCodeWrapper, CheckWrapper, MutedText, TextHeading } from "../../components/text";
import { getReranker, loadRerankers } from "../../rerankers";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { formatFloat, formatNumber } from "../../ui/format";
import { renderMarkdown } from "../../ui/md-renderer";
import { runOperation } from "../../ui/operation";
import { indentWhiteSpace, valueFrom } from "../../util";
import { PickRerankerModel } from "../../helpers/model-helpers";

export default async function (ctx) {

    const collectionId = ctx.params.id;
    const results = el.state.create(null);
    const rerankers = await loadRerankers();
    const filters = ListPickerControl({
        value: [],
        placeholder: "tags has \"finance\"",
        ariaLabel: "Add a document filter",
        itemName: "filter",
        includePendingValue: true
    });

    function buildJson() {

        const endpoint = valueFrom("apiEndpoint");

        const data = {
            __endpoint: endpoint,
            term: valueFrom("documentsSearchTerm"),
            collections: [collectionId],
            minScore: valueFrom("documentsMinScore"),
            top: valueFrom("documentsMaxDocuments"),
            reranker: valueFrom("documentsReranker"),
            filter: filters.value.length ? filters.value : undefined,
            includeReferences: valueFrom("documentsIncludeReferences")
        };

        return data;
    }

    function doSearch() {
        runOperation("Running...", async () => {
            const data = buildJson();
            const response = await api.postJson(`/api/v1${data.__endpoint}`, data);
            results.value = response.data;
        })
    }

    return Dashboard(
        {
            classList: ["full-size"]
        },
        Card(
            CardNavigation(
                CommandBar(
                    Button(
                        Icon("arrow-left-line"),
                        "Back",
                        {
                            onClick() {
                                history.go(-1);
                            }
                        }
                    ),
                    PrimaryButton(Icon("play-mini-fill"), "Run query", {
                        onClick() {
                            doSearch();
                        }
                    }),
                    ButtonRow(
                        Button(
                            Icon("braces-line"), "View code",
                            {
                                onClick() {

                                    const d = buildJson();
                                    const endpoint = d.__endpoint;
                                    delete d.__endpoint;

                                    displayAlert({
                                        title: "Collection code",
                                        message: el.fragment(
                                            "Use the code below to integrate the function you created.",
                                            BlockCodeWrapper("Example",
                                                `
curl '${window.env.apiEndpoint}api/v1${endpoint}' \\
    -H 'Authorization: Bearer $AIVAX_API_KEY' \\
    -H 'Content-Type: text/plain;charset=UTF-8' \\
    -d '${indentWhiteSpace(JSON.stringify(d, null, 4), 6, true)}'
                                                `, "bash")
                                        )
                                    })
                                }
                            }
                        ),
                        Button(
                            Icon("book-open-line"), "Read docs",
                            {
                                onClick() {
                                    window.open("https://docs.aivax.net/docs/rag/semantic-search", "_blank");
                                }
                            }
                        )
                    )
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    HorizontalSizableSection(
                        {
                            initialSize: "400px"
                        },
                        PlaygroundAside(
                            TabControl([
                                {
                                    title: "Parameters",
                                    icon: Icon("equalizer-2-fill"),
                                    contents: PlaygroundAsideContainer(
                                        SizableContainer(
                                            SizableSection("Search text",
                                                TextArea(null, null, {
                                                    id: "documentsSearchTerm",
                                                    placeholder: "Type your query..."
                                                })
                                            ),
                                            SizableSection("Parameters",
                                                Editor(
                                                    {
                                                        id: "documentsMinScore",
                                                        value: 0.4,
                                                        renderValue(value) {
                                                            return Button(formatFloat(value));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: NumberInput({ value, min: 0.01, max: 1, step: 0.05 }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Minimum score")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "documentsMaxDocuments",
                                                        value: 4,
                                                        renderValue(value) {
                                                            return Button(formatNumber(value));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: NumberInput({ value, min: 1, max: 100, step: 1 }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Maximum results")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "documentsReranker",
                                                        value: "rrf",
                                                        renderValue(value) {
                                                            return Button(getReranker(rerankers, value)?.name || value);
                                                        },
                                                        async manipulateValue(value, callback) {
                                                            callback(await PickRerankerModel({ selectedModel: value }));
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Reranker")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "apiEndpoint",
                                                        value: "/query",
                                                        renderValue(value) {
                                                            return Button(value);
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: DataPickerControl({
                                                                    data: [
                                                                        {
                                                                            value: "/query",
                                                                            text: TextHeading(null, "/query", "Queries collections for documents matching provided terms.")
                                                                        },
                                                                        {
                                                                            value: "/answer",
                                                                            text: TextHeading(null, "/answer", "Provides answers based on retrieved documents.")
                                                                        }
                                                                    ],
                                                                    value,
                                                                    allowNone: false
                                                                }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Endpoint")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "documentsIncludeReferences",
                                                        value: true,
                                                        renderValue(value) {
                                                            return CheckWrapper(value, "On", "Off");
                                                        },
                                                        manipulateValue(value, callback) {
                                                            callback(!!!value);
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Include chunked references")
                                                    )
                                                )
                                            ),
                                            SizableSection("Filters",
                                                Padding(0.5,
                                                    filters,
                                                    MutedText("Each line is a document filter. All filters must match. Press Enter to add another filter.")
                                                )
                                            )
                                        )
                                    )
                                }
                            ], {
                                type: "vertical"
                            })
                        )
                    ),
                    HorizontalSizableSection(
                        {
                            initialSize: "calc(100% - 400px)"
                        },
                        el.state.listening([results], () =>
                            PlaygroundContent(
                                results.value == null && PlaygroundDefault("play-large-line", "Click the button in the left to run a query."),
                                results.value && TabControl([
                                    {
                                        title: "Visual",
                                        icon: Icon("eye-line"),
                                        contents: el("div.rag-playground-visual",
                                            results.value.answer && el("div.rag-playground-answer.markdown-content",
                                                el.raw(renderMarkdown(results.value.answer))
                                            ),
                                            (Array.isArray(results.value) ? results.value : results.value.queryResults ?? []).length
                                                ? el("ol.rag-playground-documents",
                                                    ...(Array.isArray(results.value) ? results.value : results.value.queryResults).map(document =>
                                                        el("li",
                                                            el("div.rag-playground-document-heading",
                                                                el("strong", document.documentName || document.documentId),
                                                                el("span", formatFloat(document.score))
                                                            ),
                                                            el("div.markdown-content.wrapped-markdown-content",
                                                                el.raw(renderMarkdown(document.documentContent))
                                                            ),
                                                            document.metadata && Object.keys(document.metadata).length > 0 && Expander("Metadata",
                                                                BlockCode(JSON.stringify(document.metadata, null, 2), "json")
                                                            ),
                                                            document.referencedDocuments?.length > 0 && Expander(`${formatNumber(document.referencedDocuments.length)} referenced documents`,
                                                                ...document.referencedDocuments.map(reference => el("div.markdown-content.wrapped-markdown-content",
                                                                    el.raw(renderMarkdown(reference.documentContent))
                                                                ))
                                                            )
                                                        )
                                                    )
                                                )
                                                : MutedText("No documents matched the query.")
                                        )
                                    },
                                    {
                                        title: "JSON",
                                        icon: Icon("braces-line"),
                                        contents: BlockCode(JSON.stringify(results.value, null, 2), "json")
                                    }
                                ])
                            )
                        )
                    )
                )
            )
        )
    )
}
